import React from 'react';
import _ from 'lodash';
import {connect} from 'react-redux';
import uuid4 from 'uuid/v4';

import {formatDateOrEmptyString, formatDate} from 'utils/datetime';
import {requiresPermission, PERMISSIONS} from 'ducks/auth';

import Table from 'components/Table';
import Paginator from 'components/Paginator';
import Button from 'components/Button';
import BookingStatus from 'components/BookingStatus';
import goCardlessLogo from './assets/go-cardless-logo.svg';
import positiveIcon from './assets/positive-icon.svg';
import negativeIcon from './assets/negative-icon.svg';

import {exportPaymentsCsvInBackground as exportPaymentsCsvInBackgroundSdk} from 'sdk/payments';

import './styles.css';

const PAYMENTS_CSV_FILENAME = 'payments';

const PaymentsListTable = ({
  payments,
  pagination,
  onPageChange,
  onSort,
  sort,
  redirectToBookingDetail,
  redirectToScheduleItem,
  filters,
  locationRef,
  exportCsvInBackground,
  requiresPermission
}) => {
  const columnsMap: Array<{
    className: string;
    heading: string;
    sortField?: string;
  }> = [
    {className: 'paymentId', heading: 'Id', sortField: 'id'},
    {className: 'tenant', heading: 'Tenant'},
    {className: 'booking', heading: 'Booking'},
    {
      className: 'bookingStatus',
      heading: 'Booking Status',
      sortField: 'bookingStatus'
    },
    {
      className: 'paymentDate',
      heading: 'Payment Date',
      sortField: 'paymentDate'
    },
    {
      className: 'paymentAmount',
      heading: 'Amount',
      sortField: 'amount_applied'
    },
    {className: 'schedule', heading: 'Schedule'},
    {className: 'createdBy', heading: 'Created By'},
    {className: 'relatedObjectNumber', heading: 'Number'},
    {className: 'batchNumber', heading: 'Batch Number'},
    {className: 'creditCardNumber', heading: 'Credit Card Number'},
    {className: 'relatedObjectDate', heading: 'Rel. Date'},
    {
      className: 'invoiceDueDate',
      heading: 'Inv. Due Date',
      sortField: 'invoiceDueDate'
    },
    {className: 'total', heading: 'Total'},
    {className: 'isFullyPaid', heading: 'Fully Paid'},
    {className: 'description', heading: 'Description'},
    {className: 'reference', heading: 'Reference'},
    {className: 'goCardlessLink', heading: 'GC Link'},
    {className: 'oneOffChargeReason', heading: 'One Off Charge Reason'}
  ];

  const exportPaymentsCsvInBackground = () => {
    requiresPermission(
      {
        permission: PERMISSIONS.VIEW_CSV_EXPORT_FILE
      },
      () =>
        exportCsvInBackground({
          exportData: {
            locationRef,
            filters,
            sort
          },
          exportSdk: exportPaymentsCsvInBackgroundSdk,
          fileName: PAYMENTS_CSV_FILENAME
        })
    );
  };

  const detailLink = payment =>
    _.get(payment, `${payment.relatedObjectType}.detailLink`);
  const paymentDate = payment =>
    _.get(payment, `${payment.relatedObjectType}.date`);
  const dueDate = payment =>
    _.get(payment, `${payment.relatedObjectType}.dueDate`);
  const total = payment => _.get(payment, `${payment.relatedObjectType}.total`);
  const isFullyPaid = payment =>
    _.get(payment, `${payment.relatedObjectType}.isFullyPaid`);
  const description = payment =>
    _.get(payment, `${payment.relatedObjectType}.description`);

  return (
    <div className="PaymentsListTable">
      <div className="tableContainer">
        <div className="PaymentsListTable__header">
          <Table>
            <Table.Header>
              {columnsMap.map(column => (
                <Table.HeaderItem
                  key={column.sortField || uuid4()}
                  className={column.className}
                  sortDirection={
                    column.sortField === sort.field ? sort.direction : null
                  }
                  onSort={() => column.sortField && onSort(column.sortField)}
                  heading={column.heading}
                />
              ))}
            </Table.Header>
          </Table>
        </div>
        <div className="PaymentsListTable__content">
          <Table>
            <Table.Body>
              {payments.map(payment => (
                <Table.Row key={payment.id}>
                  <Table.Cell className="paymentId">
                    <div
                      className="colab-clickable colab-link"
                      onClick={() =>
                        redirectToScheduleItem({
                          link: _.get(payment, 'detailLink')
                        })
                      }>
                      {payment.id}
                    </div>
                  </Table.Cell>
                  <Table.Cell className="tenant">{payment.tenant}</Table.Cell>
                  <Table.Cell className="booking">
                    <div
                      className="colab-clickable colab-link"
                      onClick={() =>
                        redirectToBookingDetail({bookingId: payment.bookingId})
                      }>
                      {payment.bookingId}
                    </div>
                  </Table.Cell>
                  <Table.Cell className="bookingStatus">
                    <BookingStatus status={payment.bookingStatus} />
                  </Table.Cell>
                  <Table.Cell className="paymentDate">
                    {formatDate(payment.paymentDate)}
                  </Table.Cell>
                  <Table.Cell className="paymentAmount">
                    {payment.amount}
                  </Table.Cell>
                  <Table.Cell className="schedule">
                    {payment.scheduleType}
                  </Table.Cell>
                  <Table.Cell className="createdBy">
                    {_.get(payment, 'createdBy.fullName', '-')}
                  </Table.Cell>
                  <Table.Cell className="relatedObjectNumber">
                    {!_.isNil(detailLink(payment)) && (
                      <div
                        className="colab-clickable colab-link"
                        onClick={() =>
                          redirectToScheduleItem({link: detailLink(payment)})
                        }>
                        {_.get(payment, `${payment.relatedObjectType}.number`)}
                      </div>
                    )}
                    {_.isNil(detailLink(payment)) && '-'}
                  </Table.Cell>
                  <Table.Cell className="batchNumber">
                    {payment.batchNumber != '' && payment.batchNumber}
                    {payment.batchNumber == '' && '-'}
                  </Table.Cell>
                  <Table.Cell className="creditCardNumber">
                    {payment.creditCardNumber != '' && payment.creditCardNumber}
                    {payment.creditCardNumber == '' && '-'}
                  </Table.Cell>
                  <Table.Cell className="relatedObjectDate">
                    {!_.isNil(paymentDate(payment)) &&
                      formatDateOrEmptyString(paymentDate(payment))}
                    {_.isNil(paymentDate(payment)) && '-'}
                  </Table.Cell>
                  <Table.Cell className="invoiceDueDate">
                    {!_.isNil(dueDate(payment)) &&
                      formatDateOrEmptyString(dueDate(payment))}
                    {_.isNil(dueDate(payment)) && '-'}
                  </Table.Cell>
                  <Table.Cell className="total">
                    {!_.isNil(total(payment)) && total(payment)}
                    {_.isNil(total(payment)) && '-'}
                  </Table.Cell>
                  <Table.Cell className="isFullyPaid">
                    {!_.isNil(isFullyPaid(payment)) &&
                      isFullyPaid(payment) && (
                        <img src={positiveIcon} alt="Positive Icon" />
                      )}
                    {!_.isNil(isFullyPaid(payment)) &&
                      !_.get(
                        payment,
                        `${payment.relatedObjectType}.isFullyPaid`
                      ) && <img src={negativeIcon} alt="Negative Icon" />}
                    {_.isNil(isFullyPaid(payment)) && '-'}
                  </Table.Cell>
                  <Table.Cell className="description">
                    {!_.isNil(description(payment)) && description(payment)}
                    {_.isNil(description(payment)) && '-'}
                  </Table.Cell>
                  <Table.Cell className="reference">
                    {payment.reference}
                  </Table.Cell>
                  <Table.Cell className="goCardlessLink">
                    {!_.isNil(_.get(payment, 'gocardlessPaymentLink')) && (
                      <div
                        className="colab-clickable colab-link"
                        onClick={() =>
                          redirectToScheduleItem({
                            link: payment.gocardlessPaymentLink
                          })
                        }>
                        <img src={goCardlessLogo} alt="GC logo" />
                      </div>
                    )}

                    {_.isNil(_.get(payment, 'gocardlessPaymentLink')) && '-'}
                  </Table.Cell>
                  <Table.Cell className="oneOffChargeReason">
                    {_.get(payment, 'oneOffChargeReason', '-')}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      </div>
      <Paginator
        pagination={pagination}
        onPageChange={onPageChange}
        ActionsComponent={() => (
          <Button
            white
            className="exportButtonContainer"
            onClick={() => exportPaymentsCsvInBackground()}>
            Export Payments
          </Button>
        )}
      />
    </div>
  );
};

const mapDispatchToProps = {
  requiresPermission
};

export default connect(null, mapDispatchToProps)(PaymentsListTable);
